"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  CURRENCY_LABELS,
  SUPPORTED_CURRENCIES,
  formatCurrency,
  type CurrencyCode,
} from "@/lib/currency";
import { cn } from "./ui";

interface CurrencyPayload {
  country: string | null;
  currency: CurrencyCode;
  rates: Partial<Record<CurrencyCode, number>>;
  asOf: string | null;
  source: string;
}

interface CurrencyContextValue extends CurrencyPayload {
  loading: boolean;
  setCurrency: (currency: CurrencyCode) => Promise<void>;
  formatUsdCents: (cents: number) => string;
  formatUsdValue: (usd: number, precise?: boolean) => string;
  exactUsdCents: (cents: number) => string;
}

const DEFAULT: CurrencyContextValue = {
  country: null,
  currency: "USD",
  rates: { USD: 1 },
  asOf: null,
  source: "USD",
  loading: true,
  setCurrency: async () => {},
  formatUsdCents: (cents) => formatCurrency(cents / 100, "USD"),
  formatUsdValue: (usd, precise) => formatCurrency(usd, "USD", precise),
  exactUsdCents: (cents) => `${formatCurrency(cents / 100, "USD")} USD`,
};

const CurrencyContext = createContext<CurrencyContextValue>(DEFAULT);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<CurrencyPayload>(DEFAULT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch("/api/currency")
      .then((response) => response.json())
      .then((next: CurrencyPayload) => {
        if (alive) setPayload(next);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const setCurrency = useCallback(async (currency: CurrencyCode) => {
    setLoading(true);
    try {
      const response = await fetch("/api/currency", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ currency }),
      });
      if (!response.ok) throw new Error("Unable to change display currency");
      setPayload((await response.json()) as CurrencyPayload);
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo<CurrencyContextValue>(() => {
    const rate = payload.rates[payload.currency] ?? 1;
    return {
      ...payload,
      loading,
      setCurrency,
      formatUsdCents: (cents) =>
        formatCurrency((cents / 100) * rate, payload.currency),
      formatUsdValue: (usd, precise = false) =>
        formatCurrency(usd * rate, payload.currency, precise),
      exactUsdCents: (cents) => `${formatCurrency(cents / 100, "USD")} USD`,
    };
  }, [loading, payload, setCurrency]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

export function CurrencySelect({ className }: { className?: string }) {
  const { currency, loading, setCurrency } = useCurrency();
  return (
    <select
      aria-label="Display currency"
      title="Display currency"
      value={currency}
      disabled={loading}
      onChange={(event) => void setCurrency(event.target.value as CurrencyCode)}
      className={cn(
        "rounded-lg border border-line bg-[#13131a] px-2 py-1 text-[11px] text-muted outline-none transition hover:border-line-strong disabled:opacity-50",
        className,
      )}
    >
      {SUPPORTED_CURRENCIES.map((code) => (
        <option key={code} value={code}>
          {CURRENCY_LABELS[code]}
        </option>
      ))}
    </select>
  );
}

export function LocalMoney({
  usdCents,
  prefix = "",
  suffix = "",
  precise = false,
}: {
  usdCents: number;
  prefix?: string;
  suffix?: string;
  precise?: boolean;
}) {
  const { formatUsdCents, formatUsdValue } = useCurrency();
  const amount = precise
    ? formatUsdValue(usdCents / 100, true)
    : formatUsdCents(usdCents);
  return <>{prefix}{amount}{suffix}</>;
}

